import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/library/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAppSelector, useAppDispatch } from "@/stores/hook";
import {
  setProgramDetailId,
  setIsProgramDetails,
  setShowDealPreview,
  setDealPreviewData,
  setPendingDealCount,
  setTriggerReview,
} from "@/features/academy/stores/academyFunctionalSlice";
import type { AcademyDealPreviewData } from "@/features/academy/stores/academyFunctionalSlice";
import useCurrentProgram from "@/features/academy/api/useCurrentProgram";
import { useSyncronizer } from "@/hooks/useSyncronizer";
import ProgramHeader from "./components/ProgramHeader";
import {
  Check,
  ArrowRight,
  ChevronDown,
  GraduationCap,
  Tag,
  Upload,
  Loader2,
  X,
  FileCheck,
} from "lucide-react";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import BankNameInput from "@/features/leads/components/view-lead/components/Ongoing/components/BankNameInput";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function ProgramDetails() {
  const { programDetailId, triggerReview, dealPreviewData } = useAppSelector(
    (s) => s.academyFunctional
  );
  const dispatch = useAppDispatch();
  const { program, refetch } = useCurrentProgram(programDetailId);
  useSyncronizer(programDetailId, () => refetch());
  const { allChildren } = useChildForm();
  const { lead } = useActiveDataMutation();

  const initFromPreview = () => {
    if (!dealPreviewData)
      return {
        sel: new Map(),
        disc: new Map(),
        adv: 0,
        advM: "UPI",
        advB: "",
        advR: null as number | null,
        advRN: "",
      };
    const sel = new Map<number, { count: number }>();
    const disc = new Map<number, number>();
    for (const entry of dealPreviewData.entries) {
      const dealCount = dealPreviewData.deals.filter(
        (d) => d.variantId === entry.variantId
      ).length;
      sel.set(entry.variantId, { count: dealCount || 1 });
      if (entry.discountAllowed > 0)
        disc.set(entry.variantId, entry.discountAllowed);
    }
    return {
      sel,
      disc,
      adv: dealPreviewData.advance ?? 0,
      advM: dealPreviewData.advanceMethod || "UPI",
      advB: dealPreviewData.advanceBankName || "",
      advR: dealPreviewData.advanceReceiptDocId ?? null,
      advRN: dealPreviewData.advanceReceiptDocId ? "Uploaded receipt" : "",
    };
  };

  const init = initFromPreview();

  const [selected, setSelected] = useState<Map<number, { count: number }>>(
    init.sel
  );
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [variantDiscounts, setVariantDiscounts] = useState<Map<number, number>>(
    init.disc
  );

  const [advance, setAdvance] = useState(init.adv);
  const [advanceMethod, setAdvanceMethod] = useState(init.advM);
  const [advanceBankName, setAdvanceBankName] = useState(init.advB);
  const [advanceReceiptDocId, setAdvanceReceiptDocId] = useState<number | null>(
    init.advR
  );
  const [advanceReceiptName, setAdvanceReceiptName] = useState(init.advRN);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const advReceiptRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useDocumentMutaion();

  const handleAdvReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // No lead, nothing to file the receipt on.
    if (!file || !lead?.id) return;
    setUploadingReceipt(true);
    setAdvanceReceiptName(file.name);
    uploadFile.mutate(
      {
        file,
        // On the LEAD. The deals do not exist yet when the advance is taken,
        // and this used to send `attachedTo: "child"` with the LEAD's id —
        // which the server reads as a deal id, so the receipt was filed on
        // whichever lead owns the deal with that number. The payment row
        // still gets it through `advanceReceiptDocId` / `receiptDocId`.
        attachedTo: "leads",
        attachedScope: String(lead.id),
        category: "payment",
        name: file.name,
        attachmentLabel: "PAYMENT_RECEIPT",
      },
      {
        onSuccess: (data: any) => {
          const docId = data?.data?.id ?? data?.document?.id ?? data?.id;
          if (docId) setAdvanceReceiptDocId(docId);
          setUploadingReceipt(false);
        },
        onError: () => {
          setAdvanceReceiptName("");
          setUploadingReceipt(false);
        },
      }
    );
    if (advReceiptRef.current) advReceiptRef.current.value = "";
  };

  const handleBackClick = () => {
    dispatch(setProgramDetailId(null));
    dispatch(setIsProgramDetails(false));
  };

  const variants = useMemo(
    () =>
      (program?.variants ?? []).filter(
        (v: any) => !v.name?.startsWith("[ARCHIVED]")
      ),
    [program]
  );

  const toggleVariant = useCallback(
    (id: number) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(id)) {
          next.delete(id);
          if (expandedId === id) setExpandedId(null);
          setVariantDiscounts((d) => {
            const n = new Map(d);
            n.delete(id);
            return n;
          });
        } else {
          next.set(id, { count: 1 });
          setExpandedId(id);
        }
        return next;
      });
    },
    [expandedId]
  );

  const updateCount = useCallback((id: number, count: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const cfg = next.get(id);
      if (cfg)
        next.set(id, { ...cfg, count: Math.max(1, Math.min(20, count)) });
      return next;
    });
  }, []);

  const updateVariantDiscount = useCallback(
    (id: number, amount: number, maxPrice?: number) => {
      setVariantDiscounts((prev) => {
        const next = new Map(prev);
        let clamped = Math.max(0, amount);
        if (maxPrice != null && clamped > maxPrice) clamped = maxPrice;
        next.set(id, clamped);
        return next;
      });
    },
    []
  );

  const selectedEntries = useMemo(() => {
    const entries: {
      variant: any;
      config: { count: number };
      courseFees: number;
      subtotal: number;
      discount: number;
      maxDiscount: number;
    }[] = [];
    for (const [id, cfg] of selected) {
      const v = variants.find((x: any) => x.id === id);
      if (!v) continue;
      const fees = v.courseFees ?? 0;
      const discount = variantDiscounts.get(id) ?? 0;
      const maxDiscount = v.discountAllowed ?? 0;
      entries.push({
        variant: v,
        config: cfg,
        courseFees: fees,
        subtotal: fees * cfg.count,
        discount,
        maxDiscount,
      });
    }
    return entries;
  }, [selected, variants, variantDiscounts]);

  const grossTotal = selectedEntries.reduce((s, e) => s + e.subtotal, 0);
  const totalDeals = selectedEntries.reduce((s, e) => s + e.config.count, 0);

  const discountAmount = selectedEntries.reduce((s, e) => s + e.discount, 0);
  const netAmount = Math.max(0, grossTotal - discountAmount);
  const pending = Math.max(0, netAmount - advance);

  const existingMaxDealNum = useMemo(() => {
    if (!allChildren?.length) return 0;
    let max = 0;
    for (const c of allChildren) {
      const match = c.name?.match(/D(\d+)/i);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > max) max = num;
      }
    }
    return max;
  }, [allChildren]);

  const allDeals = useMemo(() => {
    const deals: {
      name: string;
      variantId: number;
      variantName: string;
      courseFees: number;
      discount: number;
    }[] = [];
    let dealIdx = existingMaxDealNum + 1;
    for (const entry of selectedEntries) {
      for (let i = 0; i < entry.config.count; i++) {
        deals.push({
          name: `D${dealIdx} ${entry.variant.name}`,
          variantId: entry.variant.id,
          variantName: entry.variant.name,
          courseFees: entry.courseFees,
          discount: entry.discount / entry.config.count,
        });
        dealIdx++;
      }
    }
    return deals;
  }, [selectedEntries, existingMaxDealNum]);

  useEffect(() => {
    dispatch(setPendingDealCount(totalDeals));
    return () => {
      dispatch(setPendingDealCount(0));
    };
  }, [totalDeals, dispatch]);

  useEffect(() => {
    if (triggerReview && totalDeals > 0) {
      dispatch(setTriggerReview(false));
      handleReviewAndPreview();
    }
  }, [triggerReview]);

  const handleReviewAndPreview = () => {
    const previewData: AcademyDealPreviewData = {
      programName: program?.name ?? "Unnamed Program",
      programCode: program?.code ?? "",
      entries: selectedEntries.map((e) => ({
        variantId: e.variant.id,
        variantName: e.variant.name,
        courseFees: e.courseFees,
        discountAllowed: e.discount,
        totalFees: e.courseFees - e.discount,
        advancePaid: e.variant.advancePaid ?? 0,
        remainingToPay: e.variant.remainingToPay ?? e.courseFees,
      })),
      deals: allDeals,
      grossTotal,
      discountType: "amount",
      discountValue: discountAmount,
      discountAmount,
      netAmount,
      advance,
      advanceMethod: advance > 0 ? advanceMethod : "",
      advanceBankName: advance > 0 ? advanceBankName : "",
      advanceReceiptDocId: advance > 0 ? advanceReceiptDocId : null,
      pending,
    };
    dispatch(setDealPreviewData(previewData));
    dispatch(setShowDealPreview(true));
  };

  if (!program) {
    return (
      <Card className="border-black-1 rounded-[16px]">
        <CardContent className="p-8 text-center">
          <p className="text-black-7">No program selected</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-black-1 rounded-[16px] bg-white py-0 shadow-none">
      <CardContent className="px-0">
        <div className="flex flex-col">
          <div className="px-3 pb-1">
            <ProgramHeader
              programName={program.name ?? "Unnamed Program"}
              code={program.code ?? ""}
              onBackClick={handleBackClick}
            />
          </div>

          <div className="px-4 pb-4 flex flex-col gap-5">
            <section>
              <p className="text-xs font-semibold text-[#9ba3a0] uppercase tracking-wider mb-2">
                Select Course Variants
              </p>
              <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto">
                {variants.length === 0 ? (
                  <p className="text-sm text-[#697571] py-4 text-center">
                    No variants available.
                  </p>
                ) : (
                  variants.map((v: any) => {
                    const isChecked = selected.has(v.id);
                    const isExpanded = expandedId === v.id && isChecked;
                    const cfg = selected.get(v.id);
                    const fees = v.courseFees ?? 0;
                    const maxDiscount = v.discountAllowed ?? 0;
                    const varDiscount = variantDiscounts.get(v.id) ?? 0;
                    const isDiscountOver =
                      maxDiscount > 0 && varDiscount > maxDiscount;
                    const count = cfg?.count ?? 1;

                    return (
                      <div
                        key={v.id}
                        className={`
                          rounded-xl border transition-all overflow-hidden
                          ${isChecked ? "border-[#267355] bg-[#f4fcf8]" : "border-[#e6e8e7] hover:border-[#cdd1d0]"}
                        `}
                      >
                        <div
                          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
                          onClick={() => toggleVariant(v.id)}
                        >
                          <div
                            className={`
                              size-[18px] rounded-[5px] border-2 flex items-center justify-center shrink-0 transition-all
                              ${isChecked ? "border-[#267355] bg-[#267355]" : "border-[#cdd1d0]"}
                            `}
                          >
                            {isChecked && (
                              <Check className="size-3 text-white" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-[13px] font-medium truncate ${isChecked ? "text-[#051912]" : "text-[#374741]"}`}
                            >
                              {v.name}
                            </p>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0">
                            {maxDiscount > 0 && (
                              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#FFF3E0] text-[#E65100]">
                                max -₹{fmt(maxDiscount)}
                              </span>
                            )}
                            <span className="text-[13px] font-semibold text-[#051912] tabular-nums">
                              ₹{fmt(fees)}
                            </span>
                          </div>

                          {isChecked && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedId(isExpanded ? null : v.id);
                              }}
                              className="ml-0.5 shrink-0"
                            >
                              <ChevronDown
                                className={`size-4 text-[#9ba3a0] transition-transform ${isExpanded ? "rotate-180" : ""}`}
                              />
                            </button>
                          )}
                        </div>

                        {isExpanded && cfg && (
                          <div className="px-3 pb-2.5 pt-0 border-t border-[#e0ece6]">
                            <div className="flex items-center gap-2.5 pt-2">
                              <div className="size-6 rounded-md bg-[#051912] flex items-center justify-center shrink-0">
                                <GraduationCap className="size-3 text-white" />
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => updateCount(v.id, count - 1)}
                                  className="size-6 rounded-md border border-[#cdd1d0] flex items-center justify-center text-xs font-bold text-[#374741] hover:bg-[#f0f2f1] transition-colors"
                                >
                                  −
                                </button>
                                <Input
                                  type="number"
                                  min={1}
                                  max={20}
                                  value={count}
                                  onChange={(e) =>
                                    updateCount(
                                      v.id,
                                      Number(e.target.value) || 1
                                    )
                                  }
                                  className="w-10 h-6 text-center text-xs font-semibold rounded-md border-[#cdd1d0] px-0.5 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                  onClick={() => updateCount(v.id, count + 1)}
                                  className="size-6 rounded-md border border-[#cdd1d0] flex items-center justify-center text-xs font-bold text-[#374741] hover:bg-[#f0f2f1] transition-colors"
                                >
                                  +
                                </button>
                              </div>

                              <div className="w-px h-5 bg-[#e0ece6]" />

                              <div className="size-6 rounded-md bg-[#EF5350]/10 flex items-center justify-center shrink-0">
                                <Tag className="size-3 text-[#EF5350]" />
                              </div>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#9ba3a0]">
                                  ₹
                                </span>
                                <Input
                                  type="number"
                                  min={0}
                                  max={
                                    maxDiscount > 0 ? maxDiscount : undefined
                                  }
                                  value={varDiscount || ""}
                                  onChange={(e) =>
                                    updateVariantDiscount(
                                      v.id,
                                      Number(e.target.value) || 0,
                                      fees
                                    )
                                  }
                                  placeholder={
                                    maxDiscount > 0
                                      ? `max ${fmt(maxDiscount)}`
                                      : "Discount"
                                  }
                                  className={`h-6 w-24 text-xs font-semibold rounded-md pl-5 pr-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                    isDiscountOver
                                      ? "border-red-400 text-red-600"
                                      : "border-[#cdd1d0]"
                                  }`}
                                />
                              </div>

                              <div className="flex-1 text-right shrink-0">
                                <p className="text-[10px] text-[#9ba3a0]">
                                  ₹{fmt(fees)} × {count}
                                </p>
                                <p className="text-sm font-bold text-[#051912] tabular-nums">
                                  ₹{fmt(fees * count)}
                                </p>
                              </div>
                            </div>
                            {isDiscountOver && (
                              <p className="text-[9px] text-red-500 mt-1 ml-[34px]">
                                Max discount allowed: ₹{fmt(maxDiscount)}
                              </p>
                            )}
                          </div>
                        )}

                        {isChecked && !isExpanded && cfg && (
                          <div className="px-3 pb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-[#697571]">
                                {count} enrollment{count > 1 ? "s" : ""}
                              </span>
                              {varDiscount > 0 && (
                                <span className="text-[9px] text-[#EF5350] font-medium">
                                  -₹{fmt(varDiscount)}
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] font-semibold text-[#051912] tabular-nums">
                              ₹{fmt(fees * count)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {selected.size > 0 && (
                <div className="flex items-center justify-between mt-2 px-1">
                  <span className="text-[11px] text-[#697571]">
                    {selected.size} variant{selected.size > 1 ? "s" : ""},{" "}
                    {totalDeals} deal{totalDeals > 1 ? "s" : ""}
                  </span>
                  <span className="text-[13px] font-bold text-[#051912] tabular-nums">
                    ₹{fmt(grossTotal)}
                  </span>
                </div>
              )}
            </section>

            {selected.size > 0 && (
              <section>
                <p className="text-xs font-semibold text-[#9ba3a0] uppercase tracking-wider mb-2">
                  Payment
                </p>
                <div className="rounded-xl border border-[#e6e8e7] overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#e6e8e7]">
                    <span className="text-xs text-[#697571] font-medium">
                      Gross Total
                    </span>
                    <span className="text-sm font-bold text-[#051912] tabular-nums">
                      ₹{fmt(grossTotal)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#e6e8e7]">
                    <div>
                      <span className="text-xs text-[#697571] font-medium">
                        Total Discount
                      </span>
                      {discountAmount === 0 && (
                        <p className="text-[10px] text-[#c5c9c7] mt-0.5">
                          Add discounts per variant above
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-sm font-bold tabular-nums ${discountAmount > 0 ? "text-[#EF5350]" : "text-[#9ba3a0]"}`}
                    >
                      {discountAmount > 0 ? `−₹${fmt(discountAmount)}` : "—"}
                    </span>
                  </div>

                  <div className="px-3 py-2.5 border-b border-[#e6e8e7]">
                    <span className="text-xs text-[#697571] font-medium block mb-1.5">
                      Advance Collected
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9ba3a0] text-xs">
                          ₹
                        </span>
                        <Input
                          type="number"
                          min={0}
                          value={advance || ""}
                          max={netAmount}
                          onChange={(e) =>
                            setAdvance(
                              Math.min(Number(e.target.value) || 0, netAmount)
                            )
                          }
                          placeholder="0"
                          className="h-8 rounded-lg border-[#e6e8e7] text-sm pl-6"
                        />
                      </div>
                      {advance > 0 && (
                        <>
                          <select
                            value={advanceMethod}
                            onChange={(e) => setAdvanceMethod(e.target.value)}
                            className="h-8 px-2 text-[11px] rounded-lg border border-[#e6e8e7] bg-white min-w-0"
                          >
                            <option value="UPI">UPI</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Cash">Cash</option>
                            <option value="Card">Card</option>
                            <option value="Cheque">Cheque</option>
                          </select>
                          <div className="min-w-0 flex-1">
                            <BankNameInput
                              value={advanceBankName}
                              onChange={setAdvanceBankName}
                              placeholder="Bank"
                              className="[&_input]:h-8 [&_input]:text-[11px] [&_input]:rounded-lg"
                            />
                          </div>
                          <input
                            ref={advReceiptRef}
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleAdvReceiptUpload}
                            className="hidden"
                          />
                          {advanceReceiptDocId ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="inline-flex items-center gap-1 h-8 px-2 text-[10px] font-medium rounded-lg border border-[#c1e1d5] bg-[#f0faf5] text-[#267355] max-w-[80px] truncate">
                                <FileCheck className="size-3 shrink-0" />
                                <span className="truncate">
                                  {advanceReceiptName || "Receipt"}
                                </span>
                              </span>
                              <button
                                onClick={() => {
                                  setAdvanceReceiptDocId(null);
                                  setAdvanceReceiptName("");
                                }}
                                className="size-5 flex items-center justify-center rounded hover:bg-[#fee2e2] text-[#9ba3a0]"
                              >
                                <X className="size-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => advReceiptRef.current?.click()}
                              disabled={uploadingReceipt}
                              className="h-8 px-2 flex items-center gap-1 text-[10px] font-medium rounded-lg border border-dashed border-[#c5c9c7] text-[#697571] hover:bg-[#f7f9f8] disabled:opacity-50 shrink-0"
                            >
                              {uploadingReceipt ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <Upload className="size-3" />
                              )}
                              Receipt
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-[#9ba3a0] font-medium">
                        Net
                      </p>
                      <p className="text-sm font-bold text-[#051912] tabular-nums">
                        ₹{fmt(netAmount)}
                      </p>
                    </div>
                    <div className="h-8 w-px bg-[#e6e8e7]" />
                    <div className="text-right">
                      <p className="text-[10px] text-[#9ba3a0] font-medium">
                        Pending
                      </p>
                      <p
                        className={`text-sm font-bold tabular-nums ${pending > 0 ? "text-[#EA580C]" : "text-[#267355]"}`}
                      >
                        ₹{fmt(pending)}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {selected.size > 0 && (
              <Button
                variant="primary"
                onClick={handleReviewAndPreview}
                className="w-full h-10 rounded-xl gap-2 text-sm font-semibold"
              >
                Review {totalDeals} Deal{totalDeals > 1 ? "s" : ""}
                <ArrowRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
